export const sectionColors = {
  'section-very-steep-uphill': '#8b0000',
  'section-steep-uphill': '#dc3545',
  'section-moderate-uphill': '#fd7e14',
  'section-gentle-uphill': '#ffc107',
  'section-flat': '#28a745',
  'section-gentle-downhill': '#20c997',
  'section-moderate-downhill': '#17a2b8',
  'section-steep-downhill': '#6f42c1',
  'section-very-steep-downhill': '#4b0082'
};

function throttle(func, limitMs) {
  let inThrottle = false;
  return function throttled(...args) {
    if (inThrottle) return;
    func(...args);
    inThrottle = true;
    setTimeout(() => { inThrottle = false; }, limitMs);
  };
}

export function drawProfileChart({ canvas, container, state }) {
  const data = state.fullAnalysisData;
  if (!data) return;
  const { trackpoints, sections, totalDistance } = data;
  if (!trackpoints || trackpoints.length === 0) return;
  if (!canvas || !container) return;

  const comparison = state.comparison;
  const hasOverlay = comparison?.sections?.length > 0;

  const ctx = canvas.getContext('2d');

  const minSectionDist = Math.min(...sections.map(s => s.totalDistance));
  const containerWidth = container.clientWidth || 800;
  const requiredWidth = (totalDistance / minSectionDist) * 10;

  canvas.height = hasOverlay ? 500 : 400;
  canvas.width = Math.max(containerWidth, requiredWidth);

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const margin = { top: 20, right: hasOverlay ? 70 : 50, bottom: 40, left: 50 };
  const elevationHeight = hasOverlay ? 280 : (canvas.height - margin.top - margin.bottom);
  const chartWidth = canvas.width - margin.left - margin.right;

  const elevations = trackpoints.map(p => p.ele);
  const minEle = Math.min(...elevations);
  const maxEle = Math.max(...elevations);
  const eleRange = maxEle - minEle === 0 ? 1 : maxEle - minEle;

  const getX = (distance) => margin.left + (distance / totalDistance) * chartWidth;
  const getY = (elevation) => margin.top + elevationHeight - ((elevation - minEle) / eleRange) * elevationHeight;

  // Selection + hover overlays
  const totalChartHeight = hasOverlay ? (canvas.height - margin.top - margin.bottom) : elevationHeight;
  [state.selectedSection, state.hoveredSection].forEach((section, index) => {
    if (!section) return;
    ctx.fillStyle = index === 0 ? 'rgba(108, 117, 125, 0.5)' : 'rgba(173, 181, 189, 0.5)';
    const startX = getX(section.startDistance);
    const endX = getX(section.endDistance);
    ctx.fillRect(startX, margin.top, endX - startX, totalChartHeight);
  });

  // Axes + grid
  ctx.strokeStyle = '#aaa';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(margin.left, margin.top);
  ctx.lineTo(margin.left, margin.top + elevationHeight);
  ctx.lineTo(margin.left + chartWidth, margin.top + elevationHeight);
  ctx.stroke();

  ctx.fillStyle = '#333';
  ctx.font = '12px Arial';
  ctx.textAlign = 'right';
  const yLabelCount = 5;
  for (let i = 0; i <= yLabelCount; i++) {
    const ele = minEle + (eleRange / yLabelCount) * i;
    const y = getY(ele);
    ctx.fillText(Math.round(ele) + ' m', margin.left - 8, y + 4);
    ctx.strokeStyle = '#e9ecef';
    ctx.beginPath();
    ctx.moveTo(margin.left + 1, y);
    ctx.lineTo(margin.left + chartWidth, y);
    ctx.stroke();
  }

  ctx.textAlign = 'center';
  const xLabelCount = Math.max(1, Math.floor(chartWidth / 100));
  for (let i = 0; i <= xLabelCount; i++) {
    const dist = totalDistance * (i / xLabelCount);
    const x = getX(dist);
    ctx.fillStyle = '#333';
    ctx.fillText((dist / 1000).toFixed(1) + ' km', x, margin.top + elevationHeight + 20);
  }

  // Profile sections
  ctx.lineWidth = 3;
  sections.forEach(section => {
    const startIdx = section.segments[0].startTrackpointIndex;
    const endIdx = section.segments[section.segments.length - 1].endTrackpointIndex;
    ctx.beginPath();
    ctx.strokeStyle = sectionColors[section.class] || '#000';
    const startPoint = trackpoints[startIdx];
    ctx.moveTo(getX(startPoint.cumulativeDistance), getY(startPoint.ele));
    for (let i = startIdx + 1; i <= endIdx; i++) {
      const point = trackpoints[i];
      ctx.lineTo(getX(point.cumulativeDistance), getY(point.ele));
    }
    ctx.stroke();
  });

  // Pace overlay (predicted vs actual) when comparison is available
  if (hasOverlay) {
    drawPaceOverlay(ctx, comparison.sections, {
      getX,
      margin,
      chartWidth,
      elevationTop: margin.top + elevationHeight + 30,
      paceHeight: canvas.height - margin.top - elevationHeight - 30 - margin.bottom
    });
  }
}

function drawPaceOverlay(ctx, compSections, { getX, margin, chartWidth, elevationTop, paceHeight }) {
  if (!compSections.length || paceHeight < 40) return;

  const allPaces = compSections.flatMap(s => [s.predictedPace, s.actualPace]).filter(p => p > 0 && isFinite(p));
  if (!allPaces.length) return;

  const maxPace = Math.min(Math.max(...allPaces) * 1.1, 30);
  const minPace = Math.max(Math.min(...allPaces) * 0.9, 1);
  const paceRange = maxPace - minPace || 1;

  // Pace Y: higher pace (slower) = lower on chart
  const getPaceY = (pace) => {
    const clamped = Math.max(minPace, Math.min(maxPace, pace));
    return elevationTop + ((clamped - minPace) / paceRange) * paceHeight;
  };

  // Separator line
  ctx.strokeStyle = '#adb5bd';
  ctx.lineWidth = 1;
  ctx.setLineDash([4, 4]);
  ctx.beginPath();
  ctx.moveTo(margin.left, elevationTop - 8);
  ctx.lineTo(margin.left + chartWidth, elevationTop - 8);
  ctx.stroke();
  ctx.setLineDash([]);

  // Label
  ctx.fillStyle = '#495057';
  ctx.font = 'bold 11px Arial';
  ctx.textAlign = 'left';
  ctx.fillText('Pace (min/km)', margin.left, elevationTop - 12);

  // Pace axis labels (right side)
  ctx.textAlign = 'left';
  ctx.font = '10px Arial';
  ctx.fillStyle = '#666';
  const paceSteps = 4;
  for (let i = 0; i <= paceSteps; i++) {
    const pace = minPace + (paceRange / paceSteps) * i;
    const y = getPaceY(pace);
    const mins = Math.floor(pace);
    const secs = Math.round((pace - mins) * 60);
    ctx.fillText(`${mins}:${String(secs).padStart(2, '0')}`, margin.left + chartWidth + 4, y + 3);

    ctx.strokeStyle = '#f0f0f0';
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(margin.left, y);
    ctx.lineTo(margin.left + chartWidth, y);
    ctx.stroke();
  }

  // Predicted pace (stepped line, dashed blue)
  drawSteppedPaceLine(ctx, compSections, getPaceY, getX, 'predictedPace', '#667eea', [6, 3], 2);

  // Actual pace (stepped line, solid green)
  drawSteppedPaceLine(ctx, compSections, getPaceY, getX, 'actualPace', '#28a745', [], 2.5);

  // HR overlay if available
  const hrSections = compSections.filter(s => s.avgHeartRate != null);
  if (hrSections.length > compSections.length * 0.5) {
    drawHROverlay(ctx, compSections, { getX, elevationTop, paceHeight, margin, chartWidth });
  }

  // Legend
  const legendX = margin.left + chartWidth - 220;
  const legendY = elevationTop + 2;
  ctx.font = '10px Arial';

  ctx.setLineDash([6, 3]);
  ctx.strokeStyle = '#667eea';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(legendX, legendY);
  ctx.lineTo(legendX + 20, legendY);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle = '#667eea';
  ctx.textAlign = 'left';
  ctx.fillText('Predicted', legendX + 24, legendY + 4);

  ctx.strokeStyle = '#28a745';
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.moveTo(legendX + 90, legendY);
  ctx.lineTo(legendX + 110, legendY);
  ctx.stroke();
  ctx.fillStyle = '#28a745';
  ctx.fillText('Actual', legendX + 114, legendY + 4);

  if (hrSections.length > compSections.length * 0.5) {
    ctx.strokeStyle = 'rgba(220, 53, 69, 0.6)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(legendX + 160, legendY);
    ctx.lineTo(legendX + 180, legendY);
    ctx.stroke();
    ctx.fillStyle = '#dc3545';
    ctx.fillText('HR', legendX + 184, legendY + 4);
  }
}

function drawSteppedPaceLine(ctx, compSections, getPaceY, getX, paceField, color, dashPattern, lineWidth) {
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.setLineDash(dashPattern);
  ctx.beginPath();

  let started = false;
  let cumulativeDist = 0;

  for (const section of compSections) {
    const pace = section[paceField];
    if (!pace || !isFinite(pace) || pace <= 0) {
      cumulativeDist += section.distanceKm;
      continue;
    }

    const startDist = cumulativeDist * 1000;
    const endDist = (cumulativeDist + section.distanceKm) * 1000;
    const y = getPaceY(pace);

    if (!started) {
      ctx.moveTo(getX(startDist), y);
      started = true;
    } else {
      ctx.lineTo(getX(startDist), y);
    }
    ctx.lineTo(getX(endDist), y);
    cumulativeDist += section.distanceKm;
  }
  ctx.stroke();
  ctx.setLineDash([]);
}

function drawHROverlay(ctx, compSections, { getX, elevationTop, paceHeight, margin, chartWidth }) {
  const hrs = compSections.filter(s => s.avgHeartRate != null).map(s => s.avgHeartRate);
  if (!hrs.length) return;

  const maxHR = Math.max(...hrs) * 1.05;
  const minHR = Math.min(...hrs) * 0.95;
  const hrRange = maxHR - minHR || 1;

  const getHRY = (hr) => {
    return elevationTop + paceHeight - ((hr - minHR) / hrRange) * paceHeight;
  };

  ctx.strokeStyle = 'rgba(220, 53, 69, 0.6)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();

  let started = false;
  let cumulativeDist = 0;

  for (const section of compSections) {
    const midDist = (cumulativeDist + section.distanceKm / 2) * 1000;
    cumulativeDist += section.distanceKm;

    if (section.avgHeartRate == null) continue;
    const y = getHRY(section.avgHeartRate);

    if (!started) {
      ctx.moveTo(getX(midDist), y);
      started = true;
    } else {
      ctx.lineTo(getX(midDist), y);
    }
  }
  ctx.stroke();
}

function removeLayer(map, layer) {
  if (map && layer) map.removeLayer(layer);
}

function updateHoverLayer(state) {
  const map = state.map;
  if (!map || !globalThis.L) return;
  const data = state.fullAnalysisData;
  if (!data) return;

  removeLayer(map, state.highlightLayer);
  state.highlightLayer = null;

  if (!state.hoveredSection) return;

  const { trackpoints } = data;
  const startIdx = state.hoveredSection.segments[0].startTrackpointIndex;
  const endIdx = state.hoveredSection.segments[state.hoveredSection.segments.length - 1].endTrackpointIndex;
  const sectionTrackpoints = trackpoints.slice(startIdx, endIdx + 1);
  const latlngs = sectionTrackpoints.map(p => [p.lat, p.lon]);

  state.highlightLayer = globalThis.L.polyline(latlngs, {
    color: 'red',
    weight: 5,
    opacity: 1
  }).addTo(map);
}

function updateSelectionLayer(state) {
  const map = state.map;
  if (!map || !globalThis.L) return;
  const data = state.fullAnalysisData;
  if (!data) return;

  removeLayer(map, state.selectionLayer);
  state.selectionLayer = null;

  if (!state.selectedSection) return;

  const { trackpoints } = data;
  const startIdx = state.selectedSection.segments[0].startTrackpointIndex;
  const endIdx = state.selectedSection.segments[state.selectedSection.segments.length - 1].endTrackpointIndex;
  const sectionTrackpoints = trackpoints.slice(startIdx, endIdx + 1);
  const latlngs = sectionTrackpoints.map(p => [p.lat, p.lon]);

  state.selectionLayer = globalThis.L.polyline(latlngs, {
    color: '#6f42c1',
    weight: 7,
    opacity: 0.8
  }).addTo(map);
}

export function setupProfileChartInteractions({ canvas, state, draw, onSelectionChanged } = {}) {
  if (!canvas) return;
  const throttledDraw = throttle(() => draw?.(), 16);

  canvas.addEventListener('mousemove', (event) => {
    const data = state.fullAnalysisData;
    if (!data?.sections) return;
    const { sections, totalDistance } = data;

    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const marginLeft = 50;
    const chartWidth = canvas.width - marginLeft - 50;
    const hoverDistance = (x - marginLeft) / chartWidth * totalDistance;
    const newHoveredSection = sections.find(s => hoverDistance >= s.startDistance && hoverDistance <= s.endDistance) || null;

    if (newHoveredSection !== state.hoveredSection) {
      state.hoveredSection = newHoveredSection;
      throttledDraw();
      updateHoverLayer(state);
    }
  });

  canvas.addEventListener('mouseleave', () => {
    if (!state.hoveredSection) return;
    state.hoveredSection = null;
    throttledDraw();
    updateHoverLayer(state);
  });

  canvas.addEventListener('click', (event) => {
    const data = state.fullAnalysisData;
    if (!data?.sections) return;
    const { sections, totalDistance } = data;

    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const marginLeft = 50;
    const chartWidth = canvas.width - marginLeft - 50;
    const clickedDistance = (x - marginLeft) / chartWidth * totalDistance;

    state.selectedSection = sections.find(s => clickedDistance >= s.startDistance && clickedDistance <= s.endDistance) || null;
    updateSelectionLayer(state);
    onSelectionChanged?.();
    throttledDraw();
  });
}

